import { Container, Section, Heading, Lede, Eyebrow } from "../../components/ui";
import { Seo } from "../../lib/head";
import {
  CtaBand,
  FaqSection,
  RelatedLinks,
  breadcrumbNode,
  faqPageJsonLd,
  jsonLdGraph,
  SOFTWARE_NODE,
  type Faq,
} from "../../components/marketing";
import { SITE_URL } from "../../lib/env";
import { COMPARE_PAGES, comparePath } from "./competitors";

const FAQS: Faq[] = [
  {
    q: "How do I choose consultant pharmacist software?",
    a: "Test each option on one real month of one facility: how charts get in, how many residents it screens, how recommendations are drafted, whether prescriber and DON e-signature are built in, whether invoicing comes from the same record, and how the documentation would look to a surveyor.",
  },
  {
    q: "Is it hard to change MRR software mid-year?",
    a: "Not if you switch at a monthly cycle boundary and pilot one facility first. Run that facility's month in the new platform, compare the output, then move remaining facilities at the next cycle.",
  },
  {
    q: "Do these pages compare features of other products?",
    a: "No. Each page names a product people search for and sets out the criteria worth testing, with how WeConsult Rx meets them. We do not publish claims about other vendors' products.",
  },
];

/** /compare — hub for the alternative pages; ItemList JSON-LD lists them. */
export function CompareHub() {
  const jsonLd = jsonLdGraph(
    breadcrumbNode([
      ["Home", "/"],
      ["Compare", "/compare"],
    ]),
    {
      "@type": "ItemList",
      name: "Consultant pharmacy software alternatives",
      itemListElement: COMPARE_PAGES.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `${c.competitor} alternative`,
        url: `${SITE_URL}${comparePath(c.slug)}`,
      })),
    },
    faqPageJsonLd(FAQS),
    SOFTWARE_NODE,
  );

  return (
    <>
      <Seo
        title="Consultant Pharmacy Software Alternatives | WeConsult Rx"
        description="Comparing consultant pharmacist and MRR software? Evaluation guides for teams looking at alternatives to RxPertise, GeriMed, Profiles 360, and FrameworkRxP."
        path="/compare"
        jsonLd={jsonLd}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Compare</Eyebrow>
            <Heading level={1} className="mt-3">
              Comparing consultant pharmacy software
            </Heading>
            <Lede className="mt-6">
              WeConsult Rx is consultant pharmacist software that runs the monthly medication regimen review end to
              end: chart ingest, rules-and-AI review, prescriber and DON e-signature, and invoicing, for SNF, ASC, and
              dialysis. If you are evaluating it against another platform, each guide below sets out the criteria
              worth testing.
            </Lede>
          </div>
        </Container>
      </Section>

      <RelatedLinks
        heading="Alternative guides"
        items={COMPARE_PAGES.map((c) => ({
          to: comparePath(c.slug),
          label: `${c.competitor} alternative`,
          desc: c.blurb,
        }))}
      />

      <FaqSection items={FAQS} />

      <CtaBand
        title="Compare on your own charts"
        body="Book a side-by-side demo on a sample facility, or start with a free first-facility review."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/pricing", label: "See pricing" }}
      />
    </>
  );
}
